import type { JSX } from 'react';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { Public, VerifiedUser } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { ZoneEnforcedDTO } from './Zones.tsx';

const ZoneItem = ({
    zone,
    showAccount,
    onRowSelect,
}: {
    zone: ZoneEnforcedDTO;
    showAccount: boolean;
    onRowSelect: () => void;
}): JSX.Element => {
    return (
        <TableRow hover sx={{ cursor: 'pointer' }} onClick={onRowSelect}>
            <TableCell>{zone.zoneCode}</TableCell>
            <TableCell>{zone.zoneName}</TableCell>
            <TableCell>{zone.zipCode + ' ' + zone.city}</TableCell>
            <TableCell>
                {zone.needsPermit ? (
                    <Tooltip
                        title={
                            <Localized
                                de="Privat"
                                fr="Privé"
                                it="Privato"
                                en="Private"
                            />
                        }
                        placement="right"
                    >
                        <VerifiedUser sx={{ height: 20 }} />
                    </Tooltip>
                ) : (
                    <Tooltip
                        title={
                            <Localized
                                de="Öffentlich"
                                fr="Public"
                                it="Pubblico"
                                en="Public"
                            />
                        }
                        placement="right"
                    >
                        <Public sx={{ height: 20 }} />
                    </Tooltip>
                )}
            </TableCell>
            {showAccount && (
                <TableCell>{zone.dtaAccountDescription ?? EmDash}</TableCell>
            )}
            <TableCell>{zone.zoneId}</TableCell>
        </TableRow>
    );
};

export function ZonesList({ zones }: { zones: ZoneEnforcedDTO[] }) {
    const navigate = useNavigate();

    const showAccount = zones[0]?.showAccount ?? false;

    return (
        <TableContainer>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell>Nr</TableCell>
                        <TableCell>
                            <Localized
                                de="Zonennamen"
                                fr="Nom zones"
                                it="Nome zone"
                                en="Zones name"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Ort"
                                fr="Localité"
                                it="Località"
                                en="Location"
                            />
                        </TableCell>
                        <TableCell>
                            <Localized
                                de="Zutritt"
                                fr="Accès"
                                it="Accesso"
                                en="Access"
                            />
                        </TableCell>
                        {showAccount ? (
                            <TableCell>
                                <Localized
                                    de="Gruppe"
                                    fr="Groupe"
                                    it="Gruppo"
                                    en="Group"
                                />
                            </TableCell>
                        ) : null}
                        <TableCell>ID</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {zones.map(zone => (
                        <ZoneItem
                            key={zone.zoneId}
                            zone={zone}
                            showAccount={showAccount}
                            onRowSelect={() => navigate(`${zone.zoneId}`)}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
